import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { RHYTHMS } from "../../data/tilio";
import { Reveal, RhythmIcon } from "./Shared";
import { useLenis } from "../../context";

export default function RhythmPage() {
    const { slug } = useParams();
    const rhythm = RHYTHMS.find((r) => r.slug === slug) || RHYTHMS[0];
    const lenis = useLenis();

    useEffect(() => {
        if (lenis) lenis.scrollTo(0, { immediate: true });
        else window.scrollTo(0, 0);
    }, [slug, lenis]);

    useEffect(() => {
        const names = { natura: "Natura", cialo: "Ciało", skupienie: "Skupienie" };
        const prevTitle = document.title;
        document.title = `${names[rhythm.slug] || "Co robić"} | Tilio — domki nad Liwcem`;
        const canonical = document.querySelector('link[rel="canonical"]');
        const prevCanonical = canonical?.getAttribute("href");
        if (canonical) canonical.setAttribute("href", `https://tilio.eu/rytm/${rhythm.slug}`);
        const desc = document.querySelector('meta[name="description"]');
        const prevDesc = desc?.getAttribute("content");
        if (desc) {
            desc.setAttribute(
                "content",
                `${rhythm.desc} ${rhythm.tags}. Tilio — całoroczne domki nad rzeką Liwiec w Sekłaku, około godziny od Warszawy.`
            );
        }
        return () => {
            document.title = prevTitle;
            if (canonical && prevCanonical) canonical.setAttribute("href", prevCanonical);
            if (desc && prevDesc) desc.setAttribute("content", prevDesc);
        };
    }, [rhythm]);

    return (
        <section data-testid={`rhythm-page-${rhythm.slug}`} className="px-6 pt-40 pb-24 md:pt-48 lg:pb-32">
            <div className="mx-auto max-w-3xl text-center">
                <Reveal>
                    <div className="flex justify-center text-pine">
                        <RhythmIcon slug={rhythm.slug} className="h-11 w-11" />
                    </div>
                    <p className="overline-label mt-8" data-testid="rhythm-page-eyebrow">03 — Co robić</p>
                    <h1 className="mt-5 font-serif text-[clamp(2.4rem,1.6rem+2vw,3.75rem)] tracking-[-1.2px] text-pine" data-testid="rhythm-page-title">
                        {rhythm.title}
                    </h1>
                    <p className="mt-4 text-[13px] uppercase tracking-[0.25em] text-muted-foreground">{rhythm.tags}</p>
                    <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-sage md:text-lg">{rhythm.desc}</p>
                </Reveal>
                <Reveal delay={0.1} className="mt-14 text-left">
                    <ul className="border-t border-stone" data-testid="rhythm-activities-list">
                        {rhythm.activities.map((a) => (
                            <li key={a.name} className="border-b border-stone/70 py-6" data-testid={`rhythm-activity-${a.name.toLowerCase()}`}>
                                <h2 className="font-serif text-2xl tracking-tight text-pine">{a.name}</h2>
                                <p className="mt-2 text-[17px] leading-[1.6] text-sage">{a.text}</p>
                            </li>
                        ))}
                    </ul>
                </Reveal>
                <Reveal delay={0.15} className="mt-12 flex flex-col items-center gap-7">
                    <Link
                        data-testid="rhythm-back-link"
                        to="/"
                        className="link-underline inline-flex items-center gap-2 text-base text-sage transition-colors duration-300 hover:text-pine"
                    >
                        <ArrowLeft className="h-4 w-4" strokeWidth={1.25} />
                        Wróć do strony głównej
                    </Link>
                </Reveal>
            </div>
        </section>
    );
}
